import { jsx, jsxs } from "react/jsx-runtime";
import { motion } from "motion/react";
import { useRef } from "react";
import { ALL_INDUSTRIES, getIndustryData } from "../../data/industries";
function IndustryPicker({ selected, onSelect }) {
  const scrollRef = useRef(null);
  const selectedData = getIndustryData(selected);
  return /* @__PURE__ */ jsx("section", { className: "py-24 px-6 bg-white", children: /* @__PURE__ */ jsxs("div", { className: "max-w-7xl mx-auto", children: [
    /* @__PURE__ */ jsxs("div", { className: "text-center mb-14", children: [
      /* @__PURE__ */ jsx(
        motion.div,
        {
          initial: { opacity: 0, y: 16 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.55 },
          className: "inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-xs font-semibold",
          style: { backgroundColor: "rgba(111,92,247,0.07)", border: "1px solid rgba(111,92,247,0.18)", color: "#6f5cf7", fontFamily: "'Manrope',sans-serif", letterSpacing: "0.06em" },
          children: "INDUSTRY PERSONALIZATION"
        }
      ),
      /* @__PURE__ */ jsx(
        motion.h2,
        {
          initial: { opacity: 0, y: 20 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.6, delay: 0.05 },
          style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(2rem,4vw,3rem)", fontWeight: 800, color: "#111827", letterSpacing: "-0.02em", marginBottom: "1rem" },
          children: "Who Are You?"
        }
      ),
      /* @__PURE__ */ jsx(
        motion.p,
        {
          initial: { opacity: 0, y: 14 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.6, delay: 0.1 },
          style: { fontFamily: "'Inter',sans-serif", fontSize: "1.05rem", color: "#6b7280", maxWidth: "540px", margin: "0 auto" },
          children: "Choose your industry and instantly discover AI solutions designed specifically for your business."
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: scrollRef,
        className: "grid gap-3",
        style: { gridTemplateColumns: "repeat(auto-fill,minmax(128px,1fr))" },
        children: ALL_INDUSTRIES.map((ind, i) => {
          const isSelected = selected === ind.id;
          const data = getIndustryData(ind.id);
          return /* @__PURE__ */ jsxs(
            motion.button,
            {
              initial: { opacity: 0, y: 16 },
              whileInView: { opacity: 1, y: 0 },
              viewport: { once: true },
              transition: { duration: 0.4, delay: i * 0.03 },
              onClick: () => onSelect(ind.id),
              className: "relative flex flex-col items-center gap-2 p-4 rounded-2xl text-center cursor-pointer transition-all duration-300",
              style: {
                border: isSelected ? `2px solid ${data.color}` : "2px solid #e5e7eb",
                backgroundColor: isSelected ? data.bgLight : "white",
                boxShadow: isSelected ? `0 8px 32px ${data.color}22` : "0 2px 8px rgba(0,0,0,0.04)",
                transform: isSelected ? "translateY(-4px) scale(1.04)" : void 0
              },
              whileHover: { y: -4, scale: 1.04, boxShadow: `0 8px 24px ${data.color}18` },
              whileTap: { scale: 0.97 },
              children: [
                isSelected && /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "absolute top-2 right-2 w-2 h-2 rounded-full animate-pulse",
                    style: { backgroundColor: data.color }
                  }
                ),
                /* @__PURE__ */ jsx("span", { style: { fontSize: "1.8rem", lineHeight: 1 }, children: ind.emoji }),
                /* @__PURE__ */ jsx("span", { style: {
                  fontFamily: "'Manrope',sans-serif",
                  fontWeight: isSelected ? 700 : 500,
                  fontSize: "0.78rem",
                  color: isSelected ? data.color : "#374151",
                  lineHeight: 1.3
                }, children: ind.label })
              ]
            },
            ind.id
          );
        })
      }
    ),
    selected && /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: 16 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.45 },
        className: "mt-10 rounded-3xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4",
        style: {
          background: `linear-gradient(135deg,${selectedData.bgLight} 0%,white 100%)`,
          border: `1.5px solid ${selectedData.color}25`,
          boxShadow: `0 4px 32px ${selectedData.color}10`
        },
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                className: "w-12 h-12 rounded-2xl flex items-center justify-center text-2xl",
                style: { backgroundColor: `${selectedData.color}12`, border: `1px solid ${selectedData.color}20` },
                children: ALL_INDUSTRIES.find((i) => i.id === selected)?.emoji
              }
            ),
            /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx("div", { style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: "1rem", color: "#111827" }, children: selectedData.tagline }),
              /* @__PURE__ */ jsxs("div", { style: { fontFamily: "'Inter',sans-serif", fontSize: "0.85rem", color: "#6b7280", marginTop: "2px" }, children: [
                "Showing personalized AI solutions for ",
                /* @__PURE__ */ jsx("strong", { style: { color: selectedData.color }, children: ALL_INDUSTRIES.find((i) => i.id === selected)?.label }),
                ". Everything below updates to your industry."
              ] })
            ] })
          ] }),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium",
              style: { backgroundColor: selectedData.color, color: "white", fontFamily: "'Manrope',sans-serif", whiteSpace: "nowrap" },
              children: "\u2713 Personalized View"
            }
          )
        ]
      },
      selected
    )
  ] }) });
}
export {
  IndustryPicker
};
